export type OptionKey = "A" | "B" | "C" | "D";

export interface QuestionOption {
  key: OptionKey;
  text: string;
}

export interface Question {
  id: number;
  title: string;
  options: QuestionOption[];
  correctAnswers: OptionKey[];
  note?: string;
}

export const questions: Question[] = [
  {
    id: 1,
    title: "Quelles sont les indications d'Epidyolex® ?",
    options: [
      {
        key: "A",
        text: "Epidyolex® est indiqué dans le SLG, le SD, et la STB uniquement chez l'enfant.",
      },
      {
        key: "B",
        text: "Epidyolex® est indiqué, en association au clobazam, dans le traitement adjuvant des crises d'épilepsie associées au SLG, au SD et à la STB, à partir de 2 ans et plus, chez les patients pharmacorésistants.",
      },
      {
        key: "C",
        text: "Epidyolex® est indiqué, en association au clobazam, dans le traitement adjuvant des crises d'épilepsie associées au SLG et au SD chez les patients de 2 ans et plus.",
      },
      {
        key: "D",
        text: "Epidyolex® est indiqué dans le traitement adjuvant des crises d'épilepsie associées à la STB chez les patients de 2 ans et plus.",
      },
    ],
    correctAnswers: ["C", "D"],
    note: "Informations complémentaires : SLG : syndrome de Lennox-Gastaut ; SD : syndrome de Dravet ; STB : sclérose tubéreuse de Bourneville.",
  },
  {
    id: 2,
    title: "Sur quels types de crises l'efficacité d'Epidyolex® a-t-elle été évaluée ?",
    options: [
      { key: "A", text: "Crises avec chutes (toniques, atoniques, tonico-cloniques)" },
      { key: "B", text: "Crises convulsives (toniques, atoniques, tonico-cloniques, cloniques)" },
      {
        key: "C",
        text: "Crises focales avec ou sans altération de la conscience ou perte de connaissance",
      },
      { key: "D", text: "Crises généralisées (toniques, atoniques, tonico-cloniques, cloniques)" },
    ],
    correctAnswers: ["A", "B", "C", "D"],
  },
  {
    id: 3,
    title:
      "Quels résultats d'efficacité ont été observés avec Epidyolex® 20 mg/kg/j, utilisé en association avec le clobazam chez les patients atteints du syndrome de Lennox-Gastaut ?",
    options: [
      {
        key: "A",
        text: "La fréquence des crises avec chute est réduite d'au moins 62% par rapport à l'inclusion et 55% des patients sont répondeurs*.",
      },
      {
        key: "B",
        text: "La fréquence des crises avec chute est réduite d'au moins 55% par rapport à l'inclusion et 45% des patients sont répondeurs*.",
      },
    ],
    correctAnswers: ["A"],
    note: "*répondeurs = patients ayant obtenu une réduction ≥ 50% de la fréquence des crises avec chute.",
  },
  {
    id: 4,
    title:
      "Quels sont les résultats d'efficacité d'Epidyolex® en association au clobazam dans le SLG concernant le nombre de jours sans crise avec chute ?",
    options: [
      {
        key: "A",
        text: "Dans les sous-groupes de patients traités par Epidyolex® 20mg/kg/jour en association au clobazam : les patients obtiennent jusqu'à plus de 7 jours sans crise avec chute sur une période de 28 jours.",
      },
      {
        key: "B",
        text: "Dans les sous-groupes de patients traités par Epidyolex® 20mg/kg/jour en association au clobazam : les patients obtiennent jusqu'à 5 jours sans crise avec chute sur une période de 28 jours.",
      },
      {
        key: "C",
        text: "Dans les sous-groupes de patients traités par Epidyolex® 20mg/kg/jour en association au clobazam : les patients obtiennent jusqu'à 3 jours sans crise avec chute sur une période de 28 jours.",
      },
    ],
    correctAnswers: ["A"],
  },
  {
    id: 5,
    title: "Quelles sont les doses maximales recommandées pour Epidyolex® ?",
    options: [
      {
        key: "A",
        text: "La dose maximale recommandée est de 20 mg/kg/jour dans le SLG et le SD et 25 mg/kg/jour dans la STB",
      },
      {
        key: "B",
        text: "La dose maximale recommandée est de 20 mg/kg/jour dans la STB et 25 mg/kg/jour dans le SLG et le SD",
      },
      {
        key: "C",
        text: "La dose maximale recommandée est de 25 mg/kg/jour dans la STB, le SLG et le SD",
      },
    ],
    correctAnswers: ["A"],
  },
  {
    id: 6,
    title:
      "Concernant les scores d'impression globale*, quel résultat est observé pour les patients traités par Epidyolex® 20mg/kg/jour en association au clobazam ?",
    options: [
      { key: "A", text: "46% d'amélioration vs placebo" },
      { key: "B", text: "80% d'amélioration vs placebo" },
      { key: "C", text: "67% d'amélioration vs placebo" },
      { key: "D", text: "31% d'amélioration vs placebo" },
    ],
    correctAnswers: ["B"],
    note: "* Amélioration des scores d'impression globale de changement à la dernière visite rapportés par des soignants et des patients vs inclusion.",
  },
  {
    id: 7,
    title:
      "Quels résultats d'efficacité ont été observés avec Epidyolex® 20 mg/kg/j, utilisé en association avec le clobazam chez les patients atteints du syndrome de Dravet ?",
    options: [
      {
        key: "A",
        text: "La fréquence des crises convulsives est réduite d'au moins 53% par rapport à l'inclusion et 47% des patients sont répondeurs*.",
      },
      {
        key: "B",
        text: "La fréquence des crises avec chute est réduite d'au moins 41 % par rapport à l'inclusion et 46 % des patients sont répondeurs*.",
      },
    ],
    correctAnswers: ["A"],
    note: "* répondeurs = patients ayant obtenu une réduction ≥ 50% de la fréquence des crises convulsives.",
  },
  {
    id: 8,
    title:
      "Combien de patients ont été inclus dans le programme de développement clinique dans le syndrome de Lennox-Gastaut, le syndrome de Dravet et la sclérose tubéreuse de Bourneville ?",
    options: [
      { key: "A", text: "306" },
      { key: "B", text: "632" },
      { key: "C", text: "938" },
    ],
    correctAnswers: ["C"],
  },
  {
    id: 9,
    title:
      "Dans les essais cliniques randomisés dans le SLG*, quelle est la tranche d'âge la plus représentée parmi les patients SLG* traités par cannabidiol ?",
    options: [
      { key: "A", text: "2-5 ans" },
      { key: "B", text: "6-11 ans" },
      { key: "C", text: "12-17 ans" },
      { key: "D", text: "18-55 ans" },
    ],
    correctAnswers: ["D"],
    note: "*SLG : Syndrome de Lennox-Gastaut.",
  },
  {
    id: 10,
    title: "Quelles sont les modalités de prescription d'Epidyolex® ?",
    options: [
      { key: "A", text: "Ordonnance sécurisée non renouvelable" },
      {
        key: "B",
        text: "Prescription initiale hospitalière annuelle et renouvellement réservés aux spécialistes en neurologie et neuropédiatrie",
      },
      {
        key: "C",
        text: "La prescription initiale et le renouvellement d'Epidyolex® sont réservés à tous les neurologues et neuropédiatres.",
      },
    ],
    correctAnswers: ["C"],
  },
  {
    id: 11,
    title: "Quel type de surveillance est nécessaire lors de l'instauration d'Epidyolex® ?",
    options: [
      { key: "A", text: "Surveillance cardiaque" },
      { key: "B", text: "Surveillance hépatique" },
      { key: "C", text: "Surveillance rénale" },
    ],
    correctAnswers: ["B"],
  },
  {
    id: 12,
    title: "Quelles sont la ou les contre-indications d'Epidyolex® ?",
    options: [
      {
        key: "A",
        text: "Hypersensibilité à la substance active ou à l'un des excipients (éthanol, huile de sésame, sucralose, alcool benzylique)",
      },
      {
        key: "B",
        text: "Augmentation des transaminases au-delà de trois fois la LSN et de la bilirubine au-delà de deux fois la LSN*",
      },
      { key: "C", text: "Insuffisance rénale sévère" },
    ],
    correctAnswers: ["A", "B"],
    note: "*LSN : Limite Supérieure de la Normale.",
  },
  {
    id: 13,
    title: "Quels énoncés décrivent correctement le mécanisme d'action d'Epidyolex® ?",
    options: [
      { key: "A", text: "Il agit comme les autres antiépileptiques existants" },
      {
        key: "B",
        text: "Son mécanisme d'action est différent de celui des antiépileptiques existants",
      },
      { key: "C", text: "Epidyolex® est un traitement qui se prend en association" },
      { key: "D", text: "Il bloque sélectivement les canaux sodiques voltage-dépendants" },
    ],
    correctAnswers: ["B", "C"],
  },
  {
    id: 14,
    title: "Quelle est la date des premières inclusions de patients en France dans le programme d'AAC* ?",
    options: [
      { key: "A", text: "Mars 2019" },
      { key: "B", text: "Décembre 2018" },
      { key: "C", text: "Décembre 2019" },
    ],
    correctAnswers: ["B"],
    note: "*AAC = Autorisation d'Accès Compassionnel.",
  },
  {
    id: 15,
    title:
      "Quels enseignements peut-on retenir des études SLG* et SD** sur les effets indésirables les plus fréquents d'Epidyolex® au cours du suivi des patients ?",
    options: [
      {
        key: "A",
        text: "La survenue d'effets indésirables conduit le plus souvent à une modification immédiate du schéma thérapeutique, avec une interruption du traitement lorsque aucune amélioration n'est observée.",
      },
      {
        key: "B",
        text: "Les effets indésirables les plus fréquents sont attendus et le plus souvent non graves (somnolence, baisse d'appétit, diarrhées), ils surviennent principalement en début de traitement et s'atténuent généralement avec le temps. Des adaptations thérapeutiques peuvent être proposées.",
      },
      {
        key: "C",
        text: "Les effets indésirables les plus fréquents apparaissent à tout moment du traitement, sans période de survenue privilégiée, ce qui justifie une surveillance continue tout au long de la prise en charge.",
      },
    ],
    correctAnswers: ["B"],
    note: "*SLG : Syndrome de Lennox-Gastaut ; **SD : Syndrome de Dravet.",
  },
];
